import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { format, addMonths, isSaturday, isSunday, addDays, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';

const SalidaCamaraSection = ({ entries, onChange, fechaEntradaGoya, fechaInformeEstabilidades, onFechaInformeChange }) => {
  const [weekendDialog, setWeekendDialog] = useState({
    open: false,
    entryIndex: null,
    calculatedDate: null,
    proposedDate: null,
    dayName: '',
  });
  const [manualOverrideDate, setManualOverrideDate] = useState(null);

  const parseFechaEntrada = () => {
    if (!fechaEntradaGoya) return null;
    try {
      // Format DD/MM/YYYY
      if (fechaEntradaGoya.includes('/')) {
        const [d, m, y] = fechaEntradaGoya.split('/');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      // ISO format
      return new Date(fechaEntradaGoya);
    } catch {
      return null;
    }
  };

  const addEntry = () => {
    const newNum = entries.length > 0 ? Math.max(...entries.map(e => e.numero)) + 1 : 1;
    onChange([...entries, { numero: newNum, fecha: '', metodo: '' }]);
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) return;
    const updated = entries.filter((_, i) => i !== index)
      .map((e, i) => ({ ...e, numero: i + 1 }));
    onChange(updated);
  };

  const handleMetodoChange = (index, metodo) => {
    if (metodo === 'manual') {
      const updated = [...entries];
      updated[index] = { ...updated[index], metodo, fecha: '' };
      onChange(updated);
      return;
    }

    const baseDate = parseFechaEntrada();
    if (!baseDate) {
      const updated = [...entries];
      updated[index] = { ...updated[index], metodo, fecha: '' };
      onChange(updated);
      return;
    }

    const monthsMap = { '2_meses': 2, '1_mes': 1, '3_meses': 3, '6_meses': 6 };
    const daysMap = { '7_dias': 7, '14_dias': 14, '15_dias': 15, '21_dias': 21, '45_dias': 45 };
    
    let calculated;
    if (monthsMap[metodo]) {
      calculated = addMonths(baseDate, monthsMap[metodo]);
    } else if (daysMap[metodo]) {
      calculated = addDays(baseDate, daysMap[metodo]);
    } else {
      return;
    }

    if (isSaturday(calculated) || isSunday(calculated)) {
      const proposed = isSaturday(calculated) ? subDays(calculated, 1) : addDays(calculated, 1);
      const dayName = isSaturday(calculated) ? 'sabado' : 'domingo';
      setManualOverrideDate(proposed);
      setWeekendDialog({
        open: true,
        entryIndex: index,
        calculatedDate: calculated,
        proposedDate: proposed,
        dayName,
        metodo,
      });
    } else {
      const updated = [...entries];
      updated[index] = { ...updated[index], metodo, fecha: format(calculated, 'dd/MM/yyyy') };
      onChange(updated);
    }
  };

  const handleWeekendAccept = () => {
    const { entryIndex, metodo } = weekendDialog;
    const finalDate = manualOverrideDate;
    const updated = [...entries];
    updated[entryIndex] = { ...updated[entryIndex], metodo, fecha: format(finalDate, 'dd/MM/yyyy') };
    onChange(updated);
    setWeekendDialog({ ...weekendDialog, open: false });
  };

  const handleManualDateSelect = (index, date) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], fecha: date ? format(date, 'dd/MM/yyyy') : '', metodo: 'manual' };
    onChange(updated);
  };

  const clearEntryDate = (index) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], fecha: '', metodo: '' };
    onChange(updated);
  };

  const parseEntryDate = (fechaStr) => {
    if (!fechaStr) return null;
    try {
      if (fechaStr.includes('/')) {
        const [d, m, y] = fechaStr.split('/');
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      return new Date(fechaStr);
    } catch {
      return null;
    }
  };

  const metodoLabel = (m) => {
    const labels = {
      '7_dias': '7 dias', '14_dias': '14 dias', '15_dias': '15 dias',
      '21_dias': '21 dias', '45_dias': '45 dias',
      '1_mes': '1 mes', '2_meses': '2 meses', '3_meses': '3 meses', '6_meses': '6 meses',
      'manual': 'Manual'
    };
    return labels[m] || '';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-gray-700">Fechas Salida Camara</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addEntry}
          className="text-xs"
          data-testid="add-salida-camara-btn"
        >
          <Plus className="h-3 w-3 mr-1" /> Anadir
        </Button>
      </div>

      {entries.map((entry, index) => (
        <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border" data-testid={`salida-camara-${entry.numero}`}>
          <div className="flex-shrink-0 mt-1">
            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
              {entry.numero}
            </span>
          </div>
          
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Metodo</Label>
              <Select
                value={entry.metodo || ''}
                onValueChange={(val) => handleMetodoChange(index, val)}
              >
                <SelectTrigger className="h-9 text-xs" data-testid={`salida-camara-metodo-${entry.numero}`}>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7_dias">7 dias</SelectItem>
                  <SelectItem value="14_dias">14 dias</SelectItem>
                  <SelectItem value="15_dias">15 dias</SelectItem>
                  <SelectItem value="21_dias">21 dias</SelectItem>
                  <SelectItem value="45_dias">45 dias</SelectItem>
                  <SelectItem value="1_mes">1 mes</SelectItem>
                  <SelectItem value="2_meses">2 meses</SelectItem>
                  <SelectItem value="3_meses">3 meses</SelectItem>
                  <SelectItem value="6_meses">6 meses</SelectItem>
                  <SelectItem value="manual">Calendario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Fecha</Label>
              {entry.metodo === 'manual' ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-9 justify-start text-left text-xs font-normal" data-testid={`salida-camara-fecha-${entry.numero}`}>
                      <CalendarIcon className="mr-2 h-3 w-3" />
                      {entry.fecha || <span className="text-gray-400">Seleccionar</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={parseEntryDate(entry.fecha)}
                      onSelect={(date) => handleManualDateSelect(index, date)}
                      locale={es}
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="h-9 flex items-center px-3 border rounded-md bg-white text-xs">
                  {entry.fecha ? (
                    <span className="font-medium">{entry.fecha}</span>
                  ) : (
                    <span className="text-gray-400">
                      {entry.metodo ? 'Calculando...' : 'Selecciona metodo'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {entries.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeEntry(index)}
              className="text-red-500 hover:text-red-700 h-9 w-9 p-0 mt-5 flex-shrink-0"
              data-testid={`remove-salida-camara-${entry.numero}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}

      {/* F. Informe Estabilidades */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-sm font-semibold text-gray-700">F. Informe</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal"
              data-testid="fecha-informe-estabilidades-button"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {fechaInformeEstabilidades ? (
                fechaInformeEstabilidades
              ) : (
                <span className="text-gray-400">Seleccionar fecha</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={parseEntryDate(fechaInformeEstabilidades)}
              onSelect={(date) => onFechaInformeChange(date ? format(date, 'dd/MM/yyyy') : '')}
              locale={es}
            />
            {fechaInformeEstabilidades && (
              <div className="px-3 pb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                  onClick={() => onFechaInformeChange('')}
                >
                  Borrar fecha
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Weekend Dialog */}
      <Dialog open={weekendDialog.open} onOpenChange={(open) => { if (!open) setWeekendDialog({ ...weekendDialog, open: false }); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Fecha cae en fin de semana</DialogTitle>
            <DialogDescription>
              La fecha calculada ({weekendDialog.calculatedDate ? format(weekendDialog.calculatedDate, 'dd/MM/yyyy (EEEE)', { locale: es }) : ''}) 
              cae en {weekendDialog.dayName}. 
              Se propone {weekendDialog.proposedDate ? format(weekendDialog.proposedDate, 'dd/MM/yyyy (EEEE)', { locale: es }) : ''}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <Label className="text-sm">Puedes aceptar la propuesta o seleccionar otra fecha:</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {manualOverrideDate ? format(manualOverrideDate, 'dd/MM/yyyy (EEEE)', { locale: es }) : 'Seleccionar'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={manualOverrideDate}
                  onSelect={(date) => date && setManualOverrideDate(date)}
                  locale={es}
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWeekendDialog({ ...weekendDialog, open: false })}>
              Cancelar
            </Button>
            <Button onClick={handleWeekendAccept} data-testid="weekend-accept-btn">
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalidaCamaraSection;
